import { useEffect } from 'react';

import Cookies from 'js-cookie';
import { create } from 'zustand';

import { DEFAULT_THEME } from 'app/constants/all';
import { ThemeType } from 'app/types/all';

type ThemeStoreProps = {
  theme: ThemeType;
  setTheme: (theme: ThemeType) => void;
};

const COOKIES_NAME = 'theme';

export const useThemeStore = create<ThemeStoreProps>(set => ({
  theme: DEFAULT_THEME,
  setTheme: (theme: ThemeType) => {
    const themes: ThemeType[] = ['dark', 'light'];

    // force dark theme
    theme = 'dark';

    document.documentElement.classList.remove(...themes);
    document.documentElement.classList.add(theme);

    Cookies.set(COOKIES_NAME, theme);

    set({theme});
  },
}));

export const useTheme = () => {
  const theme = useThemeStore(state => state.theme);

  return {
    theme,
    isDark: theme === 'dark',
    isLight: theme === 'light',
  };
};

export const useSetTheme = () => {
  return useThemeStore(state => state.setTheme);
};

export const useThemeInit = () => {
  const setTheme = useSetTheme();

  useEffect(() => {
    let theme = getCurrentTheme();

    if (!theme && window.matchMedia(`(prefers-color-scheme: dark)`).matches) {
      theme = 'dark';
    }

    if (theme) {
      setTheme(theme);
    }

    //

    const onKeyUp = (event: KeyboardEvent) => {
      if (
        event.ctrlKey &&
        event.shiftKey &&
        (event.code === 'KeyD' || event.code === 'KeyF')
      ) {
        setTheme(getCurrentTheme() === 'dark' ? 'light' : 'dark');
      }
    };

    window.addEventListener('keyup', onKeyUp);

    return () => {
      window.removeEventListener('keyup', onKeyUp);
    };
  }, [setTheme]);
};

const getCurrentTheme = () => {
  const theme = Cookies.get(COOKIES_NAME) as ThemeType;

  return ['light', 'dark'].includes(theme) ? theme : undefined;
};
